import { useCallback, useEffect, useState } from "react";
import { Reveal } from "./Reveal";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";

export const STATUS_LABEL = { free: "Frei", limited: "Eingeschränkt", booked: "Gebucht" };

export function useAvailability(month) {
    const [slots, setSlots] = useState([]);
    const monthKey = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
    const load = useCallback(() => {
        api.get(`/availability?month=${monthKey}`).then((r) => setSlots(r.data)).catch(() => {});
    }, [monthKey]);
    useEffect(() => {
        load();
    }, [load]);
    return { slots, reload: load };
}

export function availabilityModifiers(slots) {
    const toDate = (s) => new Date(s.date + "T12:00:00");
    return {
        free: slots.filter((s) => s.status === "free").map(toDate),
        limited: slots.filter((s) => s.status === "limited").map(toDate),
        booked: slots.filter((s) => s.status === "booked").map(toDate),
    };
}

export const availabilityClassNames = {
    free: "cal-day-free",
    limited: "cal-day-limited",
    booked: "cal-day-booked",
};

// Admin: Jeder Tag des Monats, der nicht eingeschraenkt/gebucht/gesperrt ist, bekommt einen gruenen Punkt —
// damit sofort sichtbar ist, dass dort Fuhren eingetragen werden koennen.
export function adminAvailabilityModifiers(days, month) {
    const marked = Object.fromEntries(days.map((d) => [d.date, d.status]));
    const free = [];
    const limited = [];
    const booked = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const y = month.getFullYear();
    const m = month.getMonth();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
        const iso = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        const date = new Date(y, m, d, 12);
        const st = marked[iso];
        if (st === "booked") booked.push(date);
        else if (st === "limited") limited.push(date);
        else if (date >= today || st === "free") free.push(date);
    }
    return { free, limited, booked };
}

export const Verfuegbarkeit = () => {
    const [month, setMonth] = useState(new Date());
    const { slots } = useAvailability(month);

    return (
        <section id="verfuegbarkeit" className="mx-auto max-w-7xl px-6 py-24 lg:py-32" data-testid="verfuegbarkeit-section">
            <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2">
                <Reveal>
                    <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#C88A45]">Verfügbarkeit</p>
                    <h2 className="mb-8 font-display text-4xl font-extrabold uppercase tracking-tight text-[#EDE6DA] sm:text-5xl">
                        Ist Ihr Zeitraum <span className="text-[#C88A45]">noch frei?</span>
                    </h2>
                    <p className="mb-10 max-w-md text-sm leading-relaxed text-[#9C917D]">
                        Hier sehen Sie tagesaktuell, wann Lieferungen und Forstarbeiten noch möglich sind.
                        Nicht markierte Tage sind wahrscheinlich noch frei — zu 100 % versprechen kann ich es
                        erst nach Ihrer Anfrage. Schreiben Sie einfach kurz, ich melde mich schnell zurück.
                    </p>
                    <div className="flex flex-wrap gap-6 text-sm" data-testid="availability-legend">
                        <span className="flex items-center gap-2 text-[#EDE6DA]">
                            <span className="h-2 w-2 rounded-full bg-[#7A9B5A]" /> Frei
                        </span>
                        <span className="flex items-center gap-2 text-[#EDE6DA]">
                            <span className="h-2 w-2 rounded-full bg-[#D9A441]" /> Eingeschränkt
                        </span>
                        <span className="flex items-center gap-2 text-[#9C917D] line-through">Gebucht</span>
                    </div>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="flex justify-center rounded border border-[#2E2517] bg-[#1B150E] p-6" data-testid="availability-calendar">
                        <Calendar
                            month={month}
                            onMonthChange={setMonth}
                            modifiers={availabilityModifiers(slots)}
                            modifiersClassNames={availabilityClassNames}
                            className="text-[#EDE6DA]"
                        />
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
